import React, { useRef, useState, useEffect } from "react";
import "../styles/Projects.css";
import { FaExternalLinkAlt } from "react-icons/fa";


const PROJECTS = [
   {
    title: "Guesthouse Villas",
    tag: "Business Website",
    desc: "Luxury villas website with amenities, availability, and contact — sleek responsive experience.",
    image: "/leluxededen.png",
    link: "https://www.leluxededen.com",
    tech: ["ReactJs", "CSS", "EmailJs"],
    featured: false,
  },
  {
    title: "Portfolio Website",
    tag: "Portfolio Website",
    desc: "Modern portfolio platform designed to showcase projects, skills, and achievements with a clean, responsive layout and a strong focus on personal branding and user experience.",
    image: "/royzaouk.png",
    link: "https://royzaouk.com",
    tech: ["ReactJS", "CSS", "EmailJS"],
    featured: true,
  },
  {
    title: "Safqat Platform",
    tag: "Web Application",
    desc: "Full-stack web application with user dashboard, transactions management, and responsive multi-device design.",
    image: "/safqat.png",
    link: "#",
    tech: ["ReactJS", "Node.js", "MySQL", "TailwindCSS"],
    featured: false,
  },
  {
    title: "LeSecret by Zeina",
    tag: "E-Commerce",
    desc: "Luxury cosmetics e-commerce store with product catalog, secure checkout, and full brand identity.",
    image: "/lesecret.png",
    link: "https://lesecret-byzeina.com",
    tech: ["WordPress", "WooCommerce"],
    featured: false,
  },
  {
    title: "Hair Salon",
    tag: "Business Website",
    desc: "Modern salon website featuring service showcase, testimonials, and easy contact — fully responsive.",
    image: "/hairsalon.png",
    link: "https://beautiful-twilight-cdb928.netlify.app/",
    tech: ["ReactJS", "CSS"],
    featured: false,
  },
  {
    title: "Sticks Skin",
    tag: "E-Commerce",
    desc: "Single-product e-commerce site with optimised product page and seamless checkout experience.",
    image: "/sticksskin.png",
    link: "https://sticks-skin.com",
    tech: ["WordPress", "WooCommerce"],
    featured: false,
  },
  {
    title: "Dental Clinic",
    tag: "Business Website",
    desc: "Professional clinic website with service info, doctor profile, and quick contact — clean responsive interface.",
    image: "/dentalclinic.png",
    link: "https://aquamarine-torte-084dff.netlify.app/",
    tech: ["HTML", "CSS", "JavaScript"],
    featured: false,
  },
 
  {
    title: "Fitness center",
    tag: "Business Website",
    desc: "Modern fitness center website with trainer profiles, fitness classes, map location, and social media integration — clean responsive interface.",
    image: "/gym.png",
    link: "https://bucolic-shortbread-382c40.netlify.app",
    tech: ["ReactJs", "CSS"],
    featured: false,
  },
];

/* ── hook ── */
const useVisible = (threshold = 0.08) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVis(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
};

/* ── single project card ── */
function ProjectCard({ project, index, visible }) {
  const [hovered, setHovered] = useState(false);
  const isDisabled = project.link === "#";

  return (
    <div
      className={`
        proj-card
        ${project.featured ? "proj-card--featured" : ""}
        ${visible ? "proj-card--in" : ""}
      `}
      style={{ transitionDelay: `${(index % 3) * 100}ms` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Image */}
      <div className="proj-img-wrap">
        <img src={project.image} alt={project.title} loading="lazy" />

        {/* hover overlay */}
        <div className={`proj-overlay ${hovered ? "proj-overlay--active" : ""}`}>
          {!isDisabled && (
            <a
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              className="proj-visit-btn"
              aria-label={`Visit ${project.title}`}
            >
              <FaExternalLinkAlt />
              <span>Visit Site</span>
            </a>
          )}
        </div>

        {/* tag badge */}
        <span className="proj-tag">{project.tag}</span>

        {/* index */}
        <span className="proj-index">
          {String(index + 1).padStart(2, "0")}
        </span>
      </div>

      {/* Content */}
      <div className="proj-content">
        <div className="proj-content-top">
          <h3 className="proj-title">{project.title}</h3>
          <p className="proj-desc">{project.desc}</p>
        </div>

        <div className="proj-footer">
          <div className="proj-tech">
            {project.tech.map((t) => (
              <span key={t} className="proj-chip">{t}</span>
            ))}
          </div>

          {!isDisabled && (
            <a
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              className="proj-link-btn"
              aria-label={`Open ${project.title}`}
            >
              <FaExternalLinkAlt />
            </a>
          )}
        </div>
      </div>

      {/* bottom accent line */}
      <div className="proj-line" aria-hidden="true" />
    </div>
  );
}

/* ── section ── */
function Projects() {
  const [headerRef, headerVis] = useVisible(0.1);
  const [gridRef, gridVis]     = useVisible(0.05);
  const [filter, setFilter]    = useState("All");

  const TAGS = ["All", ...Array.from(new Set(PROJECTS.map((p) => p.tag)))];

  const filtered =
    filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.tag === filter);

  return (
    <section id="projects" className="projects-section">
      <div className="about-rule" aria-hidden="true" />
      <div className="projects-container">

        {/* Header */}
        <div
          ref={headerRef}
          className={`proj-header ${headerVis ? "proj-header--in" : ""}`}
        >
          <span className="cdx-label">// our work</span>
          <h2 className="cdx-title">
            &lt;<span className="orange"> Our </span>Projects<span className="orange">/</span>&gt;
          </h2>
          <div className="cdx-rule" />
          <p className="proj-subtitle">
            A selection of real projects — websites, platforms, and digital
            products we've shipped for clients across industries.
          </p>
        </div>

        {/* Filter tabs */}
        <div
          className={`proj-filters ${headerVis ? "proj-filters--in" : ""}`}
          role="tablist"
          aria-label="Filter projects by category"
        >
          {TAGS.map((tag) => (
            <button
              key={tag}
              role="tab"
              aria-selected={filter === tag}
              className={`proj-filter-btn ${filter === tag ? "proj-filter-btn--active" : ""}`}
              onClick={() => setFilter(tag)}
            >
              {tag}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div ref={gridRef} className="proj-grid">
          {filtered.map((project, i) => (
            <ProjectCard
              key={project.title}
              project={project}
              index={i}
              visible={gridVis}
            />
          ))}
        </div>

        {/* CTA */}
        <div className={`proj-cta ${headerVis ? "proj-cta--in" : ""}`}>
          <p>Have a project in mind? Let's talk about it.</p>
          <a
            href="https://book.codixlb.com"
            target="_blank"
            rel="noopener noreferrer"
            className="cdx-btn-primary"
          >
            Book a Free Consultation
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>

      </div>
    </section>
  );
}

export default Projects;